import { ApprovalStatus, CompletionCondition } from '@insio/shared-types';

/** One company as the add-partners proposal payload carries it. */
export interface PartnerCandidateRow {
  companyId: string;
  name: string;
  city: string | null;
}

/** One invoice as the bulk approval proposal payload carries it. */
export interface ApproveInvoiceRow {
  invoiceId: string;
  invoiceNumber: string;
  supplier: string | null;
  amount: string | null;
  currency: string | null;
}

/** Housekeeping operations a bulk maintenance card can carry. */
export const MaintenanceAction = {
  REVALIDATE: 'REVALIDATE',
  ARCHIVE: 'ARCHIVE',
  UNARCHIVE: 'UNARCHIVE'
} as const;

export type MaintenanceAction = (typeof MaintenanceAction)[keyof typeof MaintenanceAction];

/** A bulk maintenance payload: one operation applied to every listed invoice. */
export interface MaintainInvoicesPayload {
  action: MaintenanceAction;
  invoices: ApproveInvoiceRow[];
}

/** A bulk transition payload: one target approval status applied to every listed invoice. */
export interface MoveInvoicesPayload {
  targetApprovalStatus: string;
  invoices: ApproveInvoiceRow[];
}

/** One requisition or purchase order as a proposal payload carries it: what its badge reads. */
export interface ProcurementRow {
  id: string;
  number: string;
  /** Null on a row stored before the approval status was carried; such a row shows no state. */
  approvalStatus: ApprovalStatus | null;
  isCancelled: boolean;
  isCompleted: boolean;
  /** Left out on a row stored before they were carried; such an unsent order reads as to send. */
  unmetCompletionConditions?: CompletionCondition[];
  /** Requisition: every line is covered by an order. */
  isOrdered: boolean;
  /** Purchase order: sent to the supplier. */
  isDispatched: boolean;
}

/** A bulk procurement payload: one target approval status applied to every listed document. */
export interface MoveProcurementPayload {
  targetApprovalStatus: string;
  documents: ProcurementRow[];
}

/** A submit-for-approval payload: the drafts the user is about to hand over. */
export interface SubmitProcurementPayload {
  documents: ProcurementRow[];
}

/**
 * Reads an untrusted submit-for-approval payload.
 * Returns null when the payload is not the shape this card renders.
 */
export function readSubmitProcurement(payload: unknown): SubmitProcurementPayload | null {
  if (!isRecord(payload)) return null;

  const documents = readProcurementRows(payload.documents);
  return documents.length > 0 ? { documents } : null;
}

const APPROVAL_STATUSES: readonly string[] = Object.values(ApprovalStatus);
const COMPLETION_CONDITIONS: readonly string[] = Object.values(CompletionCondition);

function isApprovalStatus(value: unknown): value is ApprovalStatus {
  return typeof value === 'string' && APPROVAL_STATUSES.includes(value);
}

function isCompletionCondition(value: unknown): value is CompletionCondition {
  return typeof value === 'string' && COMPLETION_CONDITIONS.includes(value);
}

/** Reads the requisition or purchase order rows of an untrusted proposal payload. */
function readProcurementRows(entries: unknown): ProcurementRow[] {
  if (!Array.isArray(entries)) return [];

  return entries.flatMap(entry => {
    if (!isRecord(entry)) return [];
    const { id, number } = entry;
    if (typeof id !== 'string' || typeof number !== 'string') return [];
    const conditions = Array.isArray(entry.unmetCompletionConditions)
      ? entry.unmetCompletionConditions.filter(isCompletionCondition)
      : undefined;
    return [
      {
        id,
        number,
        approvalStatus: isApprovalStatus(entry.approvalStatus) ? entry.approvalStatus : null,
        isCancelled: entry.isCancelled === true,
        isCompleted: entry.isCompleted === true,
        ...(conditions && { unmetCompletionConditions: conditions }),
        isOrdered: entry.isOrdered === true,
        isDispatched: entry.isDispatched === true
      }
    ];
  });
}

/** One sync connection as the retry payload carries it. */
export interface RetrySyncRow {
  instanceId: string;
  name: string;
  entityType: string;
}

/** Reversible partner flag changes a bulk maintenance card can carry. */
export const PartnerAction = {
  ARCHIVE: 'ARCHIVE',
  UNARCHIVE: 'UNARCHIVE',
  MARK_SUPPLIER: 'MARK_SUPPLIER',
  MARK_CUSTOMER: 'MARK_CUSTOMER'
} as const;

export type PartnerAction = (typeof PartnerAction)[keyof typeof PartnerAction];

/** One partner as the bulk maintenance payload carries it. */
export interface MaintainPartnerRow {
  partnerId: string;
  name: string;
  companyId: string | null;
}

/** A bulk partner payload: one change applied to every listed partner. */
export interface MaintainPartnersPayload {
  action: PartnerAction;
  partners: MaintainPartnerRow[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Reads the partner rows out of an untrusted proposal payload.
 * Returns null when the payload is not the shape this card renders.
 */
export function readPartnerRows(payload: unknown): PartnerCandidateRow[] | null {
  if (!isRecord(payload) || !Array.isArray(payload.partners)) return null;

  const rows = payload.partners.flatMap(entry => {
    if (!isRecord(entry)) return [];
    const { companyId, name } = entry;
    if (typeof companyId !== 'string' || typeof name !== 'string') return [];
    const address = isRecord(entry.address) ? entry.address : undefined;
    return [{ companyId, name, city: optionalString(address?.city) }];
  });

  return rows.length > 0 ? rows : null;
}

/**
 * Reads the invoice rows out of an untrusted bulk-approval payload.
 * Returns null when the payload is not the shape this card renders.
 */
export function readApproveInvoiceRows(payload: unknown): ApproveInvoiceRow[] | null {
  if (!isRecord(payload) || !Array.isArray(payload.invoices)) return null;

  const rows = payload.invoices.flatMap(entry => {
    if (!isRecord(entry)) return [];
    const { invoiceId, invoiceNumber } = entry;
    if (typeof invoiceId !== 'string' || typeof invoiceNumber !== 'string') return [];
    return [
      {
        invoiceId,
        invoiceNumber,
        supplier: optionalString(entry.supplier),
        amount: optionalString(entry.amount),
        currency: optionalString(entry.currency)
      }
    ];
  });

  return rows.length > 0 ? rows : null;
}

/**
 * Reads an untrusted bulk-transition payload — the target approval status plus its invoices.
 * Returns null when the payload is not the shape this card renders.
 */
export function readMoveInvoices(payload: unknown): MoveInvoicesPayload | null {
  if (!isRecord(payload) || typeof payload.targetApprovalStatus !== 'string') return null;

  const invoices = readApproveInvoiceRows(payload);
  return invoices === null
    ? null
    : { targetApprovalStatus: payload.targetApprovalStatus, invoices };
}

/**
 * Reads the email addresses out of an untrusted bulk-invite payload.
 * Returns null when the payload is not the shape this card renders.
 */
export function readInviteEmails(payload: unknown): string[] | null {
  if (!isRecord(payload) || !Array.isArray(payload.emails)) return null;

  const emails = payload.emails.filter(
    (entry): entry is string => typeof entry === 'string' && entry.length > 0
  );

  return emails.length > 0 ? emails : null;
}

export interface InviteUserPayload {
  email: string;
  orgUnitLabel: string | null;
  asOrgUnitManager: boolean;
}

/**
 * Reads an untrusted single-invite payload — the address, and the org unit with the head flag.
 * Returns null when the payload is not the shape this card renders.
 */
export function readInviteUser(payload: unknown): InviteUserPayload | null {
  if (!isRecord(payload) || typeof payload.email !== 'string' || payload.email.length === 0) {
    return null;
  }
  const orgUnitLabel = readInviteOrgUnitLabel(payload);
  return {
    email: payload.email,
    orgUnitLabel,
    asOrgUnitManager: orgUnitLabel !== null && payload.asOrgUnitManager === true
  };
}

/** Reads the org unit label ("code — name") of an untrusted invite payload; null when absent. */
export function readInviteOrgUnitLabel(payload: unknown): string | null {
  if (!isRecord(payload) || typeof payload.orgUnitLabel !== 'string') return null;
  return payload.orgUnitLabel.length > 0 ? payload.orgUnitLabel : null;
}

/**
 * Reads an untrusted bulk-procurement payload — the target approval status plus its documents.
 * Returns null when the payload is not the shape this card renders.
 */
export function readMoveProcurement(payload: unknown): MoveProcurementPayload | null {
  if (!isRecord(payload) || typeof payload.targetApprovalStatus !== 'string') return null;

  const documents = readProcurementRows(payload.documents);
  return documents.length > 0
    ? { targetApprovalStatus: payload.targetApprovalStatus, documents }
    : null;
}

/**
 * Reads the connections out of an untrusted sync-retry payload.
 * Returns null when the payload is not the shape this card renders.
 */
export function readRetrySyncs(payload: unknown): RetrySyncRow[] | null {
  if (!isRecord(payload) || !Array.isArray(payload.connections)) return null;

  const rows = payload.connections.flatMap(entry => {
    if (!isRecord(entry)) return [];
    const { instanceId, name, entityType } = entry;
    if (typeof instanceId !== 'string' || typeof name !== 'string') return [];
    return [{ instanceId, name, entityType: typeof entityType === 'string' ? entityType : '' }];
  });

  return rows.length > 0 ? rows : null;
}

function isPartnerAction(value: unknown): value is PartnerAction {
  return (
    value === PartnerAction.ARCHIVE ||
    value === PartnerAction.UNARCHIVE ||
    value === PartnerAction.MARK_SUPPLIER ||
    value === PartnerAction.MARK_CUSTOMER
  );
}

/**
 * Reads an untrusted bulk-partner payload — the change plus its partners.
 * Returns null when the payload is not the shape this card renders.
 */
export function readMaintainPartners(payload: unknown): MaintainPartnersPayload | null {
  if (!isRecord(payload) || !isPartnerAction(payload.action)) return null;
  if (!Array.isArray(payload.partners)) return null;

  const partners = payload.partners.flatMap(entry => {
    if (!isRecord(entry)) return [];
    const { partnerId, name } = entry;
    if (typeof partnerId !== 'string' || typeof name !== 'string') return [];
    return [{ partnerId, name, companyId: optionalString(entry.companyId) }];
  });

  return partners.length > 0 ? { action: payload.action, partners } : null;
}

function isMaintenanceAction(value: unknown): value is MaintenanceAction {
  return (
    value === MaintenanceAction.REVALIDATE ||
    value === MaintenanceAction.ARCHIVE ||
    value === MaintenanceAction.UNARCHIVE
  );
}

/**
 * Reads an untrusted bulk-maintenance payload — the operation plus its invoices.
 * Returns null when the payload is not the shape this card renders.
 */
export function readMaintainInvoices(payload: unknown): MaintainInvoicesPayload | null {
  if (!isRecord(payload) || !isMaintenanceAction(payload.action)) return null;

  const invoices = readApproveInvoiceRows(payload);
  return invoices === null ? null : { action: payload.action, invoices };
}
